import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authActivityEvents } from '../../services/authActitivityEvents';

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for real-time data

// Fetch KPI Summary for Auth Events
export const fetchAuthEventsKPI = createAsyncThunk(
  'authEvents/fetchKPI',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { kpiSummary } = getState().authEvents;
      const now = Date.now();

      // Check cache
      if (kpiSummary.lastFetched && now - kpiSummary.lastFetched < CACHE_DURATION) {
        return { cached: true };
      }

      const data = await authActivityEvents.kpiSummary("kpi-summary");
      return { cached: false, data };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load KPI summary');
    }
  }
);

// Fetch Timeline Data
export const fetchAuthEventsTimeline = createAsyncThunk(
  'authEvents/fetchTimeline',
  async (mode = 'realtime', { getState, rejectWithValue }) => {
    try {
      const { timeline } = getState().authEvents;
      const now = Date.now();

      // Check cache for this mode
      const cachedMode = timeline.cache[mode];
      if (cachedMode && now - cachedMode.timestamp < CACHE_DURATION) {
        return { cached: true, mode };
      }

      // Fetch timeline data based on mode
      const data = await authActivityEvents.getTimeline(mode);

      // console.log(("this is timline data",data));
      
      return { cached: false, mode, data };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load timeline data');
    }
  }
);

const authEventsSlice = createSlice({
  name: 'authEvents',
  initialState: {
    // KPI Summary
    kpiSummary: {
      data: {
        current_threat_score: 0,
        total_events: 0,
        critical_events: 0,
        high_risk_events: 0,
      },
      loading: false,
      error: null,
      lastFetched: null,
    },

    // Timeline Data
    timeline: {
      data: [],
      loading: false,
      error: null,
      mode: 'realtime', // 'realtime' | 'historical'
      cache: {
        // Store separate cache for each mode
        realtime: { data: [], timestamp: null },
        historical: { data: [], timestamp: null },
      },
    },

    // Time Filter
    timeFilter: 'today', // 'today' | '24h' | '7d' | 'all'
  },
  reducers: {
    // Set time filter
    setTimeFilter: (state, action) => {
      state.timeFilter = action.payload;
    },

    // Set timeline mode
    setTimelineMode: (state, action) => {
      state.timeline.mode = action.payload;
    },

    // Clear KPI cache
    clearKPICache: (state) => {
      state.kpiSummary.lastFetched = null;
    },

    // Clear timeline cache
    clearTimelineCache: (state, action) => {
      if (action.payload) {
        // Clear specific mode
        state.timeline.cache[action.payload] = { data: [], timestamp: null };
      } else {
        // Clear all
        state.timeline.cache = {
          realtime: { data: [], timestamp: null },
          historical: { data: [], timestamp: null },
        };
      }
    },

    // Clear all caches
    clearAllAuthEventsCaches: (state) => {
      state.kpiSummary.lastFetched = null;
      state.timeline.cache = {
        realtime: { data: [], timestamp: null },
        historical: { data: [], timestamp: null },
      };
    },

    // Manual update for real-time data
    updateTimelineRealtime: (state, action) => {
      const mode = state.timeline.mode;
      state.timeline.cache[mode].data = [action.payload, ...state.timeline.cache[mode].data];
      state.timeline.cache[mode].timestamp = Date.now();
      state.timeline.data = state.timeline.cache[mode].data;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch KPI Summary
      .addCase(fetchAuthEventsKPI.pending, (state) => {
        state.kpiSummary.loading = true;
        state.kpiSummary.error = null;
      })
      .addCase(fetchAuthEventsKPI.fulfilled, (state, action) => {
        state.kpiSummary.loading = false;
        const { cached, data } = action.payload;

        if (!cached) {
          state.kpiSummary.data = data;
          state.kpiSummary.lastFetched = Date.now();
        }
      })
      .addCase(fetchAuthEventsKPI.rejected, (state, action) => {
        state.kpiSummary.loading = false;
        state.kpiSummary.error = action.payload;
      })

      // Fetch Timeline
      .addCase(fetchAuthEventsTimeline.pending, (state) => {
        state.timeline.loading = true;
        state.timeline.error = null;
      })
      .addCase(fetchAuthEventsTimeline.fulfilled, (state, action) => {
        state.timeline.loading = false;
        const { cached, mode, data } = action.payload;

        if (!cached) {
          state.timeline.cache[mode] = {
            data,
            timestamp: Date.now(),
          };
        }

        // Update current timeline data
        state.timeline.data = state.timeline.cache[mode].data;
      })
      .addCase(fetchAuthEventsTimeline.rejected, (state, action) => {
        state.timeline.loading = false;
        state.timeline.error = action.payload;
      });
  },
});

export const {
  setTimeFilter,
  setTimelineMode,
  clearKPICache,
  clearTimelineCache,
  clearAllAuthEventsCaches,
  updateTimelineRealtime,
} = authEventsSlice.actions;

export default authEventsSlice.reducer;